import { z } from 'zod';
import { containsProfanity } from '@/lib/profanityFilter';

export const itemFormSchema = z.object({
  game: z.string().min(1, 'Please select a game'),
  custom_game_name: z.string().max(50, 'Game name must be less than 50 characters').optional()
    .refine((val) => !val || !containsProfanity(val), { message: 'Please use appropriate language' }),
  language: z.string().min(1, 'Please select a language'),
  title: z.string().min(2, 'Title must be at least 2 characters').max(100, 'Title must be less than 100 characters')
    .refine((val) => !containsProfanity(val), { message: 'Please use appropriate language' }),
  category: z.enum(['sealed', 'singles', 'slabs', 'lots', 'accessories']),
  singles_condition: z.enum(['mint', 'near_mint', 'lightly_played', 'moderately_played', 'heavily_played', 'damaged']).optional(),
  sealed_condition: z.enum(['factory_sealed', 'factory_sealed_minor', 'factory_sealed_major', 'resealed', 'opened']).optional(),
  accessory_condition: z.enum(['new', 'like_new', 'good', 'fair']).optional(),
  accessory_type: z.enum(['playmat', 'sleeves', 'deck_box', 'binder', 'toploader', 'display_case', 'storage', 'dice', 'other']).optional(),
  slab_grade: z.coerce.number().min(1).max(10).optional(),
  grading_company: z.string().optional(),
  custom_grading_company: z.string().max(50, 'Company name must be less than 50 characters').optional(),
  certification_number: z.string().optional(),
  card_number: z.string().optional(),
  rarity: z.string().optional(),
  finish: z.string().optional(),
  finish_label: z.string().nullable().optional(),
  set_name: z.string().optional(),
  is_first_edition: z.boolean().default(false),

  is_error_card: z.boolean().default(false),
  error_type: z.string().optional(),
  product_type: z.string().optional(),
  is_premium_grade: z.boolean().default(false),
  quantity: z.coerce.number().min(1, 'Quantity must be at least 1'),
  purchase_date: z.string().optional(),
  cost_basis: z.string().optional(),
  description: z.string().max(2000, 'Description must be less than 2000 characters').optional()
    .refine((val) => !val || !containsProfanity(val), { message: 'Please use appropriate language' }),
  is_listed: z.boolean().default(false),
  asking_price: z.string().optional(),
  is_negotiable: z.boolean().default(false),
  accepts_trades: z.boolean().default(false),
  ships_anywhere: z.boolean().default(false),
  target_price: z.string().optional(),
  // Lot-specific fields
  lot_type: z.enum(['singles_lot', 'sealed_lot', 'mixed_lot']).optional(),
  approx_card_count: z.coerce.number().min(1).optional(),
  box_count: z.string().max(500, 'Sealed products list must be less than 500 characters').optional()
    .refine((val) => !val || !containsProfanity(val), { message: 'Please use appropriate language' }),
  lot_highlights: z.string().max(1000, 'Highlights must be less than 1000 characters').optional()
    .refine((val) => !val || !containsProfanity(val), { message: 'Please use appropriate language' }),
  lot_eras: z.array(z.string()).optional(),
  lot_languages: z.array(z.string()).optional(),
  includes_holos: z.boolean().default(false),
  includes_ultras: z.boolean().default(false),
  includes_slabs: z.boolean().default(false),
  includes_promos: z.boolean().default(false),
  includes_first_editions: z.boolean().default(false),
  includes_full_arts: z.boolean().default(false),
  includes_vintage: z.boolean().default(false),
  includes_promo_boxes: z.boolean().default(false),
  includes_etbs: z.boolean().default(false),
  includes_booster_boxes: z.boolean().default(false),
  includes_booster_packs: z.boolean().default(false),
  includes_tins: z.boolean().default(false),
  includes_premium_collections: z.boolean().default(false),
  includes_bundles: z.boolean().default(false),
  includes_booster_cases: z.boolean().default(false),
  includes_build_battle: z.boolean().default(false),
  includes_starter_decks: z.boolean().default(false),
  includes_blisters: z.boolean().default(false),
  lot_condition: z.enum(['mint', 'near_mint', 'lightly_played', 'moderately_played', 'heavily_played', 'damaged']).optional(),
});

export type ItemFormData = z.infer<typeof itemFormSchema>;

export const STEP_LABELS = {
  1: 'What is it?',
  2: 'Details & Listing',
} as const;
